import {ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {Observable, Subject, take, takeUntil} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {BsModalService} from 'ngx-bootstrap/modal';
import {ConfirmDeleteModalComponent} from '../../../../components/confirm-delete-modal/confirm-delete-modal.component';
import {FormSubmitService} from '../../../../services/form-submit.service';
import {TableFunctionList} from '../../../../entities/table-function-list.entity';
import {Backend} from '@generated-backend-types';
import TimeTrackingEmployee = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingEmployee.TimeTrackingEmployee;

export interface EmployeeSubResourceService {
  forEmployee(employee: TimeTrackingEmployee): void;
  delete(id: string): Observable<void>;
}

@Directive()
export abstract class TimeTrackingConfigListBaseComponent implements OnInit, OnDestroy {
  @Input({required: true}) public employee!: TimeTrackingEmployee;
  public isAdding = false;
  public reloadTable$ = new EventEmitter<void>();
  protected onDestroy$: Subject<void> = new Subject<void>();
  public tableFunctionList: TableFunctionList = {
    delete: rowData => this.confirmDelete((rowData as { id: string }).id),
  };

  protected constructor(
    protected readonly formSubmitService: FormSubmitService,
    protected readonly service: EmployeeSubResourceService,
    protected readonly translate: TranslateService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly bsModalService: BsModalService,
  ) {
  }

  protected abstract readonly translationPrefix: string;

  public abstract form: AbstractControl;

  protected abstract resetForm(): void;

  protected abstract submitForm(): Observable<unknown>;

  public ngOnInit() {
    this.service.forEmployee(this.employee);
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }

  public startAdding() {
    this.resetForm();
    this.form.markAsUntouched();
    this.form.markAsPristine();
    this.isAdding = true;
  }

  public cancel() {
    this.isAdding = false;
  }

  public save() {
    this.submitForm().subscribe(() => {
      this.isAdding = false;
      this.reloadTable$.emit();
    });
  }

  private confirmDelete(id: string) {
    this.bsModalService.show(ConfirmDeleteModalComponent, {
      initialState: {
        modelId: id,
        dialogTranslation: this.translate.instant(`${this.translationPrefix}.delete.confirm_title`),
        successTranslation: this.translate.instant(`${this.translationPrefix}.delete.success`),
        errorTranslation: this.translate.instant(`${this.translationPrefix}.delete.error`),
        observable: this.service.delete(id),
      }
    }).content?.deleteSuccess.pipe(
      takeUntil(this.onDestroy$),
      take(1)
    ).subscribe(() => {
      this.reloadTable$.emit();
      this.cdr.markForCheck();
    });
  }
}
